import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { TaskRoute } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { removeModalFromQueue } from "@/store/slices/ui";
import { ModalState, ModalType } from "@/types/modals";

import CreateCustomTaskModal from "./Modals/CustomTask/CreateCustomTaskModal";
import CustomTaskAddPhotosModal from "./Modals/CustomTask/CustomTaskAddPhotosModal";
import ExportFileModal from "./Modals/ExportFile";
import { ExportPayment } from "./Modals/ExportPayment";
import KpiProgressModal from "./Modals/KpiProgress";
import MessageModal from "./Modals/Message";
import PromoModal from "./Modals/Promo";
import StateModal from "./Modals/State";
import { UncompletedCheckoutsModal } from "./Modals/UncompletedCheckouts";
import { UncompletedTaskModal } from "./Modals/UncompletedTask";
import { UnscheduledPointCompletionModal } from "./Modals/UnscheduledPointCompletion";
import { UploadRetryModal } from "./Modals/UploadRetryModal";
import { YesterdayOpenedCheckoutsModal } from "./Modals/YesterdayOpenedCheckoutsModal";

// Пріоритети модалок (чим більше - тим вищий пріоритет)
const MODAL_PRIORITIES: Partial<Record<ModalType, number>> = {
    [ModalType.YesterdayOpenedCheckouts]: 70,
    [ModalType.KpiProgress]: 60,
    [ModalType.Promos]: 50,
    [ModalType.UnscheduledPointCompletion]: 40,
    [ModalType.State]: 40,
    [ModalType.Messages]: 30,
    [ModalType.Exports]: 20,
    [ModalType.UncompletedTasks]: 10,
    [ModalType.UncompletedCheckouts]: 0,
};

export const ModalsContainer: React.FC = () => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const isInitialDataLoading = useAppSelector(
        (state) => state.offline.isInitialDataLoading,
    );
    const modalsQueueList = useAppSelector((state) => state.ui.modalsQueueList);

    // Один стейт для поточної модалки
    const [currentModal, setCurrentModal] = useState<ModalState | null>(null);

    // Сортуємо модалки за пріоритетом (від більшого до меншого)
    const sortedModals = useMemo(
        () =>
            [...modalsQueueList].sort((a, b) => {
                const priorityA = MODAL_PRIORITIES[a.type] ?? 0;
                const priorityB = MODAL_PRIORITIES[b.type] ?? 0;
                return priorityB - priorityA;
            }),
        [modalsQueueList],
    );

    useEffect(() => {
        if (sortedModals.length === 0) {
            setCurrentModal(null);
            return;
        }

        const reminderModals = [
            ModalType.UncompletedTasks,
            ModalType.UncompletedCheckouts,
        ];

        const isOnTaskRoute = Object.values(TaskRoute).some((route) =>
            pathname.includes(route),
        );

        if (isOnTaskRoute) {
            const filteredModal = sortedModals.find(
                (m) => !reminderModals.includes(m.type),
            );
            setCurrentModal(filteredModal ?? null);
            return;
        }

        setCurrentModal(sortedModals[0] ?? null);
    }, [pathname, sortedModals]);

    const handleCloseModal = useCallback(
        (modalKey: string) => {
            setCurrentModal(null);
            dispatch(removeModalFromQueue(modalKey));
        },
        [dispatch],
    );

    if (isInitialDataLoading || !currentModal) {
        return null;
    }

    switch (currentModal.type) {
        case ModalType.Promos:
            return (
                <PromoModal
                    key={currentModal.id}
                    payload={currentModal.payload}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.Messages:
            return (
                <MessageModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.KpiProgress:
            return (
                <KpiProgressModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.UnscheduledPointCompletion:
            return (
                <UnscheduledPointCompletionModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.Exports:
            return (
                <ExportFileModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.UncompletedCheckouts:
            return (
                <UncompletedCheckoutsModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.UncompletedTasks:
            return (
                <UncompletedTaskModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.State:
            return (
                <StateModal
                    key={currentModal.id}
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.CustomTaskAddPhotos:
            return (
                <CustomTaskAddPhotosModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );
        case ModalType.CreateCustomTask:
            return (
                <CreateCustomTaskModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.YesterdayOpenedCheckouts:
            return (
                <YesterdayOpenedCheckoutsModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                />
            );

        case ModalType.UploadRetry:
            return (
                <UploadRetryModal
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        case ModalType.ExportPayment:
            return (
                <ExportPayment
                    onCloseModal={() => handleCloseModal(currentModal.id)}
                    payload={currentModal.payload}
                />
            );

        default:
            return null;
    }
};
